import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DateRange } from '@factorialco/f0-react/dist/experimental'
import { IrisValueObjectsFormatHintEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { getRenderedDefFromTableId } from 'modules/ai_reports/components/DatasourceTypeRenderer/tableRendererRegistry'
import { datasourceTypeFromId } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'
import { DateNavigationFilter } from 'modules/ai_reports/components/DateNavigationFilter'
import { Layout } from 'modules/ai_reports/components/Layout'
import { ReportWidget } from 'modules/ai_reports/components/ReportWidget'
import { SaveModal } from 'modules/ai_reports/components/SaveModal'
import { assertDefined } from 'modules/ai_reports/lib/assertDefined'
import { exportReport } from 'modules/ai_reports/lib/exportReport'
import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'
import { useNavConfirmModal } from 'modules/ai_reports/lib/useNavConfirmModal'
import { useSaveReport } from 'modules/ai_reports/lib/useSaveReport'
import { ReportType } from 'modules/ai_reports/pages/ReportDetailPage/types'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type ReportDetailProps = {
  report: ReportType
}

function filtersToStrings(filters: AiReportsResult['filters']) {
  const result: { snapshotDate?: string; dateRange?: { from: string; to: string } } = {}

  if (filters && filters.snapshotDate) {
    result.snapshotDate = filters.snapshotDate.toISOString()
  }

  if (filters && filters.dateRange) {
    result.dateRange = {
      from: filters.dateRange.from.toISOString(),
      to: filters.dateRange.to.toISOString(),
    }
  }

  return result
}

function filtersToDates(filters: {
  snapshotDate?: string
  dateRange?: { from: string; to: string }
}) {
  const result: AiReportsResult['filters'] = {}

  if (filters && filters.snapshotDate) {
    result.snapshotDate = new Date(filters.snapshotDate)
  }

  if (filters && filters.dateRange) {
    result.dateRange = {
      from: new Date(filters.dateRange.from),
      to: new Date(filters.dateRange.to),
    }
  }

  return result
}

export const ReportDetail = ({ report }: ReportDetailProps) => {
  const { company } = useCurrentSession()

  const [localReport, setLocalReport] = useState(report)

  const [saveModalOpen, setSaveModalOpen] = useState(false)

  type InitialStateType = {
    type: 'text2sql'
    currentDataset: string
    sqlAnswer: AiReportsResult['sqlAnswer'] | { sql: string; description: string }
    filters: AiReportsResult['filters']
  }
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { state, setState } = useCocreationAgent<AiReportsResult, InitialStateType>({
    name: 'ai-reports-workflow',
    initialState: {
      type: 'text2sql',
      currentDataset: report.datasource.id,
      sqlAnswer: {
        sql: report.widgets[0]?.query ?? '',
        description: report.widgets[0]?.description ?? '',
        column_format_hints: report.formatHints.map((hint) => ({
          column_alias: hint.columnDisplayName,
          column_format: hint.formatHint,
        })),
      },
      filters: filtersToDates(report.filters),
    },
    initialMessage: i18n.t('ai_reports.ai.report_detail_message', {
      reportName: report.name,
    }),
  })

  useAutoOpenChat()

  const { NavConfirmModal, setNavigationBlocked } = useNavConfirmModal()

  const { saveReport } = useSaveReport({
    onSaveSuccess: (reportId: string) => {
      setNavigationBlocked(false)
      if (report.id !== reportId) {
        setTimeout(() => {
          navigate(navigation.aiReports.reports.reportId.fullPath({ reportId }))
        }, 100)
      }
    },
  })

  // TODO: The resource header doesn't allow to mutate the actions state
  const canSave = true
  const handleSave = async ({
    name,
    description,
    saveAsNew,
    filters,
  }: {
    name: string
    description: string
    saveAsNew: boolean
    filters: AiReportsResult['filters']
  }) => {
    if (!canSave) return

    await saveReport({
      id: saveAsNew ? undefined : localReport.id,
      name: name,
      datasourceId: localReport.datasource.id,
      description: description,
      widgets: localReport.widgets,
      filters: filtersToStrings(filters),
      columnFormatHints: state.sqlAnswer?.column_format_hints.map((hint) => ({
        columnDisplayName: hint.column_alias,
        formatHint: hint.column_format as IrisValueObjectsFormatHintEnum,
      })),
    })
  }

  // Track when the AI state changes to mark as having unsaved changes
  useEffect(() => {
    // If the current state differs from the initial report, mark as unsaved
    if (state.sqlAnswer && state.sqlAnswer.sql !== localReport.widgets[0]?.query) {
      setNavigationBlocked(true)
    }
  }, [state.sqlAnswer, localReport.widgets, setNavigationBlocked])

  useEffect(() => {
    if (
      !!state.sqlAnswer?.sql &&
      localReport.widgets[0] &&
      localReport.widgets[0]?.query !== state.sqlAnswer?.sql
    ) {
      setLocalReport((prev: ReportType) => {
        assertDefined(prev.widgets[0], 'Previous widgets is undefined')
        return {
          ...prev,
          widgets: [
            {
              ...prev.widgets[0],
              query: state.sqlAnswer.sql,
              description: state.sqlAnswer.description,
            },
          ],
        }
      })
    }
  }, [state.sqlAnswer.sql, state.sqlAnswer.description])

  const rendererDef = getRenderedDefFromTableId(localReport.datasource.id)
  const dateNavigatorConfig = rendererDef?.dateNavigatorConfig?.(filtersToDates(report.filters))

  const [dateFilter, setDateFilter] = useState<DateRange | undefined>(
    report.filters.dateRange
      ? {
          from: new Date(report.filters.dateRange.from),
          to: new Date(report.filters.dateRange.to),
        }
      : {
          from: new Date(report.filters.snapshotDate),
          to: undefined,
        }
  )

  const handleExport = () => {
    if (!localReport || !localReport.widgets[0] || !localReport.datasource) return
    exportReport(
      localReport.name,
      localReport.widgets[0].query,
      company.id,
      datasourceTypeFromId(localReport.datasource.id),
      state.filters || {}
    )
  }

  return (
    <>
      <ResponsivePage.Body
        variant="fullscreen"
        onActionClick={(key) => {
          switch (key) {
            case 'export':
              handleExport()
              break
            case 'save':
              setSaveModalOpen(true)
              break
          }
        }}
      >
        <Layout
          header={
            dateNavigatorConfig && (
              <DateNavigationFilter
                config={dateNavigatorConfig}
                onSelect={(value) => {
                  setDateFilter(value?.value)
                }}
              />
            )
          }
          paddingX="s16"
        >
          {localReport &&
            localReport.widgets.map((widget, index) => (
              <ReportWidget
                key={index}
                sqlQuery={widget.query}
                description={widget.description}
                dateFilter={dateFilter}
                tableId={localReport.datasource.id}
                columnFormatHints={state.sqlAnswer?.column_format_hints ?? []}
                reportName={localReport.name}
                onChangeFilters={(filters) =>
                  setState((prev) => {
                    assertDefined(prev, 'Previous state is undefined')
                    return { ...prev, filters }
                  })
                }
              />
            ))}
          <NavConfirmModal />
          <SaveModal
            showSaveAsNewReport
            open={saveModalOpen}
            name={localReport.name}
            description={localReport.description}
            onClose={() => setSaveModalOpen(false)}
            onClickSave={({ name, description, saveAsNew }) =>
              handleSave({ name, description, saveAsNew, filters: state.filters })
            }
          />
        </Layout>
      </ResponsivePage.Body>
    </>
  )
}
